import pluralize from 'pluralize';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  type WorkspaceAgentGuidedFlowDestination,
  type WorkspaceAgentGuidedFlowSubmissionParams,
} from '@air/analytics';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PrivateWorkspaceAgentGuidedVariantChooseBoardMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantChooseBoardMenu';
import { PrivateWorkspaceAgentGuidedVariantGrayscaleSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantGrayscaleSettings';
import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { PrivateWorkspaceAgentGuidedVariantRemoveBackgroundSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantRemoveBackgroundSettings';
import { PrivateWorkspaceAgentGuidedVariantRoundedCornersSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantRoundedCornersSettings';
import { PrivateWorkspaceAgentGuidedVariantScaleOrCropSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantScaleOrCropSettings';
import { PrivateWorkspaceAgentGuidedVariantSmartResizeSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantSmartResizeSettings';
import { PrivateWorkspaceAgentGuidedVariantWatermarkSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantWatermarkSettings';
import {
  WorkspaceAgentGuidedVariantFlow,
  type WorkspaceAgentGuidedVariantSelectBoardMenuRenderProps,
  type WorkspaceAgentGuidedVariantSettingsRenderer,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantFlow';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import { PrivateWorkspaceAgentBoardPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentBoardPillChip';
import { type GuidedVariantEditId } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';
import {
  getWorkspaceAgentHeroStructuredDisplay,
  getWorkspaceAgentPanelZeroStateSuggestionById,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { usePrivateWorkspaceAgentGuidedVariantFlowTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentGuidedVariantFlowTracking';
import { usePrivateWorkspaceAgentGuidedVariantSelection } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentGuidedVariantSelection';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import {
  usePrivateWorkspaceAgentPanelComposerPills,
  usePrivateWorkspaceAgentPanelSession,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentSingleBoardPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { toWorkspaceAgentGuidedVariantsAnalyticsEditId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantAnalytics';
import {
  applyWorkspaceAgentGuidedVariantOutputInstruction,
  applyWorkspaceAgentGuidedVariantOutputInstructionToJsonContent,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  getWorkspaceAgentGuidedVariantSaveOptions,
  WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS,
  type WorkspaceAgentGuidedVariantChosenBoard,
  type WorkspaceAgentGuidedVariantSaveDestination,
  type WorkspaceAgentGuidedVariantSaveOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';
import { useWorkspaceAgentComposerLock } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerLockProvider';
import {
  useWorkspaceAgentComposerSeed,
  type WorkspaceAgentComposerSeedBoardPill,
} from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { centralizedBoardIdSelector, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

const GUIDED_VARIANT_COMPOSER_LOCK_SOURCE_ID = 'guided-variant';

export type PrivateWorkspaceAgentGuidedVariantFlowProps = {
  onClose: () => void;
  onStepChange: () => void;
};

const FALLBACK_BOARD_TITLE = 'Untitled board';

const buildGuidedFlowSubmission = (
  destination: WorkspaceAgentGuidedFlowDestination,
  output: WorkspaceAgentGuidedVariantOutput,
  editId: GuidedVariantEditId,
): WorkspaceAgentGuidedFlowSubmissionParams => ({
  destination,
  edit_id: toWorkspaceAgentGuidedVariantsAnalyticsEditId(editId),
  format: output.format,
  has_suffix: output.suffix.trim().length > 0,
});

const toWorkspaceAgentGuidedVariantSeedBoardPill = (
  destination: WorkspaceAgentGuidedVariantSaveDestination,
): WorkspaceAgentComposerSeedBoardPill | undefined => {
  switch (destination.type) {
    case 'versions':
      return undefined;
    case 'board':
      return { boardId: destination.boardId, title: destination.boardTitle };
    case 'new-subboard':
      return { boardId: destination.boardId };
    default: {
      const exhaustive: never = destination;

      return exhaustive;
    }
  }
};

export const PrivateWorkspaceAgentGuidedVariantFlow = memo(
  ({ onClose, onStepChange }: PrivateWorkspaceAgentGuidedVariantFlowProps) => {
    const store = useAirStore();
    const { hasExplicitSelection, scopePills } = usePrivateWorkspaceAgentPanelComposerPills();
    const { blockIfCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
    const boardId = useSelector(centralizedBoardIdSelector);
    const [pendingGenerateRequest, setPendingGenerateRequest] =
      useState<WorkspaceAgentGuidedVariantGenerateRequest | null>(null);
    const canceledBoardPill = useMemo(
      (): WorkspaceAgentSingleBoardPill | null => (boardId ? { boardId, id: boardId, type: 'board' } : null),
      [boardId],
    );
    const { sessionId } = usePrivateWorkspaceAgentPanelSession();
    const { seedComposer } = useWorkspaceAgentComposerSeed();
    const { trackGuidedVariantFlowEditSelected, trackGuidedVariantFlowOpened } =
      usePrivateWorkspaceAgentGuidedVariantFlowTracking({ chatId: sessionId });
    const handleCanceledRestart = useCallback(() => {
      trackGuidedVariantFlowOpened({ entry: 'canceled_restart' });
    }, [trackGuidedVariantFlowOpened]);
    const guidedVariantFlowInitialEdit = useWorkspaceAgentGuidedVariantFlowStore(
      (state) => state.guidedVariantFlowInitialEdit,
    );
    const { lockComposer, unlockComposer } = useWorkspaceAgentComposerLock();

    // Lock is driven by the visible wizard step (chooser, settings, save location). Unmount always
    // releases so a send or close cannot leave the composer disabled.
    useIsomorphicLayoutEffect(() => {
      return () => {
        unlockComposer(GUIDED_VARIANT_COMPOSER_LOCK_SOURCE_ID);
      };
    }, [unlockComposer]);

    const saveLocations = useMemo(
      () => getWorkspaceAgentGuidedVariantSaveOptions({ hasCurrentBoard: Boolean(boardId) }),
      [boardId],
    );

    const { assetCount, skipCounts } = usePrivateWorkspaceAgentGuidedVariantSelection(scopePills);

    const question = useMemo(
      () =>
        assetCount > 0
          ? `${assetCount.toLocaleString()} ${pluralize('asset', assetCount)} selected. What do you want to do?`
          : 'What do you want to do?',
      [assetCount],
    );

    const handleFollowUp = useCallback(
      (suggestionId: string) => {
        const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(suggestionId);

        if (!suggestion) {
          return;
        }

        seedComposer(suggestion.prompt, {
          sendImmediately: hasExplicitSelection,
          source: 'hero_chip',
          structuredDisplay: hasExplicitSelection ? getWorkspaceAgentHeroStructuredDisplay(suggestionId) : undefined,
        });
        onClose();
      },
      [hasExplicitSelection, onClose, seedComposer],
    );

    const handleRename = useCallback(() => handleFollowUp('hero-bulk-rename'), [handleFollowUp]);
    const handleTag = useCallback(() => handleFollowUp('hero-bulk-tag-metadata'), [handleFollowUp]);

    const handleGenerate = useCallback(
      (request: WorkspaceAgentGuidedVariantGenerateRequest) => {
        if (blockIfCreditsDepleted()) {
          return false;
        }

        setPendingGenerateRequest(request);
      },
      [blockIfCreditsDepleted],
    );

    const handleDiscardSave = useCallback(() => {
      setPendingGenerateRequest(null);
      unlockComposer(GUIDED_VARIANT_COMPOSER_LOCK_SOURCE_ID);
    }, [unlockComposer]);

    const handleRestart = useCallback(() => {
      lockComposer(GUIDED_VARIANT_COMPOSER_LOCK_SOURCE_ID);
    }, [lockComposer]);

    const handleSelectDestination = useCallback(
      (
        destination: WorkspaceAgentGuidedVariantSaveDestination,
        output: WorkspaceAgentGuidedVariantOutput,
        editId: GuidedVariantEditId,
        analyticsDestination: WorkspaceAgentGuidedFlowDestination,
      ) => {
        if (!pendingGenerateRequest || blockIfCreditsDepleted()) {
          return;
        }

        // The wizard is finished. Release the lock in this same turn so the seeded send is not
        // swallowed by the composer guard.
        unlockComposer(GUIDED_VARIANT_COMPOSER_LOCK_SOURCE_ID);

        const boardPill = toWorkspaceAgentGuidedVariantSeedBoardPill(destination);

        if (pendingGenerateRequest.kind === 'jsonContent') {
          seedComposer('', {
            boardPill,
            guidedFlowSubmission: buildGuidedFlowSubmission(analyticsDestination, output, editId),
            jsonContent: applyWorkspaceAgentGuidedVariantOutputInstructionToJsonContent({
              jsonContent: pendingGenerateRequest.build(destination, output),
              output,
            }),
            sendImmediately: true,
            source: 'guided_flow',
            structuredDisplay: pendingGenerateRequest.structuredDisplay,
          });
        } else {
          seedComposer(
            applyWorkspaceAgentGuidedVariantOutputInstruction({
              content: pendingGenerateRequest.build(destination, output),
              output,
            }),
            {
              boardPill,
              guidedFlowSubmission: buildGuidedFlowSubmission(analyticsDestination, output, editId),
              sendImmediately: true,
              source: 'guided_flow',
              structuredDisplay: pendingGenerateRequest.structuredDisplay,
            },
          );
        }

        onClose();
      },
      [blockIfCreditsDepleted, onClose, pendingGenerateRequest, seedComposer, unlockComposer],
    );

    const handleSelectBoardFromMenu = useCallback(
      (
        board: WorkspaceAgentGuidedVariantChosenBoard,
        output: WorkspaceAgentGuidedVariantOutput,
        editId: GuidedVariantEditId,
      ) => {
        handleSelectDestination(
          {
            boardId: board.id,
            boardTitle: board.title || FALLBACK_BOARD_TITLE,
            type: 'board',
          },
          output,
          editId,
          'selected_board',
        );
      },
      [handleSelectDestination],
    );

    const handleSelectSaveLocation = useCallback(
      (
        optionId: WorkspaceAgentGuidedVariantSaveOptionId,
        output: WorkspaceAgentGuidedVariantOutput,
        editId: GuidedVariantEditId,
      ) => {
        switch (optionId) {
          case WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.VERSIONS:
            handleSelectDestination({ type: 'versions' }, output, editId, 'versions');
            return;
          case WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.THIS_BOARD: {
            if (!boardId) {
              reportErrorToBugsnag({
                error: new Error(`Guided variant save option ${optionId} requires a current board`),
                context: 'Workspace agent guided variant save location missing current board',
              });
              return;
            }

            handleSelectDestination(
              {
                boardId,
                boardTitle: centralizedBoardTitleSelector(store.getState()) || FALLBACK_BOARD_TITLE,
                type: 'board',
              },
              output,
              editId,
              'this_board',
            );
            return;
          }
          case WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.NEW_SUBBOARD: {
            if (!boardId) {
              reportErrorToBugsnag({
                error: new Error(`Guided variant save option ${optionId} requires a current board`),
                context: 'Workspace agent guided variant save location missing current board',
              });
              return;
            }

            handleSelectDestination({ boardId, type: 'new-subboard' }, output, editId, 'new_subboard');
            return;
          }
          case WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.SELECT_BOARD:
            return;
        }
      },
      [boardId, handleSelectDestination, store],
    );

    const renderSelectBoardMenu = useCallback(
      ({ onSelectBoard }: WorkspaceAgentGuidedVariantSelectBoardMenuRenderProps) => (
        <PrivateWorkspaceAgentGuidedVariantChooseBoardMenu onSelectBoard={onSelectBoard} />
      ),
      [],
    );

    const settings = useMemo<Record<GuidedVariantEditId, WorkspaceAgentGuidedVariantSettingsRenderer>>(
      () => ({
        grayscale: (onGenerate) => <PrivateWorkspaceAgentGuidedVariantGrayscaleSettings onGenerate={onGenerate} />,
        'remove-background': (onGenerate) => (
          <PrivateWorkspaceAgentGuidedVariantRemoveBackgroundSettings onGenerate={onGenerate} />
        ),
        resize: (onGenerate) => <PrivateWorkspaceAgentGuidedVariantScaleOrCropSettings onGenerate={onGenerate} />,
        'rounded-corners': (onGenerate) => (
          <PrivateWorkspaceAgentGuidedVariantRoundedCornersSettings onGenerate={onGenerate} />
        ),
        'smart-resize': (onGenerate) => (
          <PrivateWorkspaceAgentGuidedVariantSmartResizeSettings onGenerate={onGenerate} />
        ),
        watermark: (onGenerate) => <PrivateWorkspaceAgentGuidedVariantWatermarkSettings onGenerate={onGenerate} />,
      }),
      [],
    );

    return (
      <WorkspaceAgentGuidedVariantFlow
        canceledBoardChip={
          canceledBoardPill ? <PrivateWorkspaceAgentBoardPillChip pill={canceledBoardPill} /> : undefined
        }
        hasSelection={hasExplicitSelection}
        initialEdit={guidedVariantFlowInitialEdit}
        onCanceledRestart={handleCanceledRestart}
        onClose={onClose}
        onEditSelected={trackGuidedVariantFlowEditSelected}
        onDiscardSave={handleDiscardSave}
        onGenerate={handleGenerate}
        onRename={handleRename}
        onRestart={handleRestart}
        onSelectBoardFromMenu={handleSelectBoardFromMenu}
        onSelectSaveLocation={handleSelectSaveLocation}
        onStepChange={onStepChange}
        onTag={handleTag}
        question={question}
        renderSelectBoardMenu={renderSelectBoardMenu}
        saveLocations={saveLocations}
        settings={settings}
        skipCounts={skipCounts}
      />
    );
  },
);

PrivateWorkspaceAgentGuidedVariantFlow.displayName = 'PrivateWorkspaceAgentGuidedVariantFlow';
